import React, { useState, useEffect } from "react";
import { Users, Phone, Send, Loader2, CheckCircle, XCircle, ChevronDown, Edit2, MessageSquare } from "lucide-react";
import axios from "axios";
import DoneByBadge from "./DoneByBatch";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const RELATION_OPTIONS = ["Father", "Mother", "Husband", "Wife", "Brother", "Sister"];

const inputCls =
  "w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-transparent transition disabled:bg-gray-50 disabled:cursor-not-allowed";

const selectCls =
  "w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-transparent transition appearance-none bg-white disabled:bg-gray-50 disabled:cursor-not-allowed";

const SelectWrapper = ({ children }) => (
  <div className="relative">
    {children}
    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
  </div>
);

const InlineBanner = ({ msg, type }) => {
  if (!msg) return null;
  return (
    <div className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 mt-2 ${
      type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
    }`}>
      {type === "success" ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {msg}
    </div>
  );
};

const ReferenceDetails = ({
  workerId,
  initialData = null,
  onUpdate,
  isRefChecked = false,
  doneByName = null,
  doneAt = null,
  canEditVerification = false,
}) => {
  const [relation, setRelation] = useState("");
  const [relationName, setRelationName] = useState("");
  const [relationPhone, setRelationPhone] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });
  const [hasReferenceData, setHasReferenceData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [isReferenced, setIsReferenced] = useState(false);
  // local meta that updates optimistically after a save
  const [localMeta, setLocalMeta] = useState({ byName: doneByName, at: doneAt });

  useEffect(() => { setLocalMeta({ byName: doneByName, at: doneAt }); }, [doneByName, doneAt]);

  const isValidReferenceData = (data) => {
    if (!data) return false;
    if (!data.RelativeName || !data.RelativePhone || !data.RelativeType) return false;
    if (!data.RelativeName.trim()) return false;
    if (!data.RelativePhone.trim() || data.RelativePhone.length !== 10) return false;
    return true;
  };

  const fetchReferenceData = async () => {
    if (!workerId) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/get-worker-reference-check`,
        { workerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success && response.data.data?.RefCheck) {
        const refData = response.data.data.RefCheck;
        if (isValidReferenceData(refData)) {
          setRelation(refData.RelativeType || "");
          setRelationName(refData.RelativeName || "");
          setRelationPhone(refData.RelativePhone || "");
          setIsReferenced(refData.isReferenced ?? isRefChecked);
          setRemarks(refData.Remarks || refData.remarks || "");
          setHasReferenceData(true);
          setIsEditing(false);
        } else {
          setRelation(refData.RelativeType || "");
          setRelationName(refData.RelativeName || "");
          setRelationPhone(refData.RelativePhone || "");
          setIsReferenced(refData.isReferenced ?? isRefChecked);
          setRemarks(refData.Remarks || refData.remarks || "");
          setHasReferenceData(false);
          setIsEditing(true);
        }
      } else {
        setHasReferenceData(false);
        setIsEditing(true);
      }
    } catch {
      setHasReferenceData(false);
      setIsEditing(true);
    } finally {
      setIsLoading(false);
      setInitialFetchDone(true);
    }
  };

  useEffect(() => {
    if (initialData && isValidReferenceData(initialData)) {
      setRelation(initialData.RelativeType || "");
      setRelationName(initialData.RelativeName || "");
      setRelationPhone(initialData.RelativePhone || "");
      setIsReferenced(initialData.isReferenced ?? isRefChecked);
      setRemarks(initialData.Remarks || initialData.remarks || "");
      setHasReferenceData(true);
      setIsEditing(false);
      setInitialFetchDone(true);
    } else if (!initialFetchDone && workerId) {
      fetchReferenceData();
    }
  }, [initialData, workerId, initialFetchDone, isRefChecked]);

  useEffect(() => {
    if (!hasReferenceData) setIsReferenced(isRefChecked);
  }, [isRefChecked, hasReferenceData]);

  const flash = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 3500);
  };

  const validateForm = () => {
    if (!relation) return flash("Please select a relation", "error");
    if (!relationName.trim()) return flash("Please enter the relation's name", "error");
    if (!relationPhone.trim()) return flash("Please enter a phone number", "error");
    if (relationPhone.length !== 10) return flash("Please enter a valid 10-digit phone number", "error");
    if (!remarks.trim()) return flash("Please enter remarks", "error");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/update-reference-check`,
        {
          workerId,
          RelativeName: relationName.trim(),
          RelativePhone: relationPhone,
          RelativeType: relation,
          isReferenceChecked: isReferenced,
          remarks: remarks.trim(),
        },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response.data.success) {
        setHasReferenceData(true);
        setIsEditing(false);
        // optimistic meta update
        setLocalMeta({ byName: response.data.doneByName || localMeta.byName, at: new Date().toISOString() });
        flash(response.data.message || "Reference details saved successfully.", "success");
        if (onUpdate) onUpdate({ RelativeName: relationName.trim(), RelativePhone: relationPhone, RelativeType: relation, isReferenced, remarks: remarks.trim() });
      } else {
        throw new Error(response.data.message || "Failed to save reference details");
      }
    } catch (err) {
      flash(err.response?.data?.message || err.message || "Failed to save reference details.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => { setIsEditing(true); setMessage({ text: "", type: "success" }); };
  const handleCancelEdit = () => { if (hasReferenceData) setIsEditing(false); else setIsEditing(false); setMessage({ text: "", type: "success" }); };

  if (isLoading) {
    return (
      <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center gap-1.5 mb-2 text-emerald-600">
          <Users className="h-4 w-4" />
          <span className="text-xs font-semibold">Reference Details</span>
        </div>
        <div className="flex justify-center items-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
          <span className="ml-1.5 text-xs text-gray-600">Loading reference data...</span>
        </div>
      </div>
    );
  }

  // ── View mode ──
  if (hasReferenceData && !isEditing) {
    return (
      <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-emerald-600">
            <Users className="h-4 w-4" />
            <span className="text-xs font-semibold">Reference Details</span>
          </div>
          <button
  onClick={canEditVerification ? handleEdit : undefined}
  disabled={!canEditVerification}
  title={!canEditVerification ? "Not permitted" : undefined}
  className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition ${
    canEditVerification
      ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
      : "text-gray-300 cursor-not-allowed"
  }`}
>
  <Edit2 className="h-3.5 w-3.5" /> Edit
</button>
        </div>

        <div className="p-2 rounded-md border border-green-200 bg-green-50">
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs w-full">
              <p className="font-medium text-green-700 mb-1">
                Reference {isReferenced ? "Verified ✓" : "Saved (Not Verified)"}
              </p>
              <p className="text-gray-600">Relation: {relation}</p>
              <p className="text-gray-600">Name: {relationName}</p>
              <p className="text-gray-600">Phone: {relationPhone}</p>
              <p className="text-gray-600 mt-1">
                Status:{" "}
                {isReferenced
                  ? <span className="text-green-600 font-medium">Verified</span>
                  : <span className="text-amber-600 font-medium">Not Verified</span>}
              </p>
              {remarks && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <p className="text-gray-600 font-medium">Remarks:</p>
                  <p className="text-gray-700 bg-white/50 p-1.5 rounded mt-0.5">{remarks}</p>
                </div>
              )}
              {/* ── done-by INSIDE the green box ── */}
              <DoneByBadge doneByName={localMeta.byName} doneAt={localMeta.at} label="Updated" />
            </div>
          </div>
        </div>

        <InlineBanner msg={message.text} type={message.type} />
      </div>
    );
  }

  // ── Edit / Add mode ──
  return (
    <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-emerald-600">
          <Users className="h-4 w-4" />
          <span className="text-xs font-semibold">
            {hasReferenceData ? "Edit Reference Details" : "Add Reference Details"}
          </span>
        </div>
        {hasReferenceData && isEditing && (
          <button onClick={handleCancelEdit} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded-md hover:bg-gray-200 transition">
            Cancel
          </button>
        )}
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">Relation <span className="text-red-500">*</span></label>
            <SelectWrapper>
              <select value={relation} onChange={(e) => setRelation(e.target.value)} className={selectCls} disabled={isSubmitting}>
                <option value="">Select</option>
                {RELATION_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </SelectWrapper>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">Name <span className="text-red-500">*</span></label>
            <input type="text" value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Full name" className={inputCls} disabled={isSubmitting} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">Phone <span className="text-red-500">*</span></label>
          <div className="relative">
            <Phone className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input type="tel" value={relationPhone} onChange={(e) => setRelationPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" className={`${inputCls} pl-7`} disabled={isSubmitting} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">Remarks <span className="text-red-500">*</span></label>
          <div className="relative">
            <MessageSquare className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter remarks (mandatory)" rows={2} className={`${inputCls} pl-7 resize-none`} disabled={isSubmitting} />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isReferenced} onChange={(e) => setIsReferenced(e.target.checked)} className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" disabled={isSubmitting} />
            <span className="text-sm text-gray-700">Mark as Verified</span>
          </label>
        </div>

        <div className="pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !relation || !relationName.trim() || !relationPhone || relationPhone.length !== 10 || !remarks.trim()}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition font-medium shadow-sm ${
              (!relation || !relationName.trim() || !relationPhone || relationPhone.length !== 10 || !remarks.trim() || isSubmitting) ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>
              : <><Send className="h-4 w-4" /> {hasReferenceData ? "Update Reference Details" : "Save Reference Details"}</>}
          </button>
        </div>

        <p className="text-[10px] text-gray-500 text-center">All fields are mandatory</p>
      </div>

      <InlineBanner msg={message.text} type={message.type} />
    </div>
  );
};

export default ReferenceDetails;